import { useMutation } from "@tanstack/react-query";
import type { IUserInfo } from "./use-user-info";
import { QueryKeys } from "@/constants";
import type { IRequestIdResponse } from "@/services/request-id";
import { requestIdService } from "@/services/request-id";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { getErrorMessage } from "@/lib/error-message-utils";
import { lowerCaseComparison } from "@/lib/string-util";
import { API_STATUS_MESSAGE } from "@/services/endpoints";
import { useDispatch, useSelector } from "react-redux";
import { updateRequestedAt } from "@/store/request-id-card/requestId-slice";
import type { RootState } from "@/store/store";

export const useRequestId = () => {
  const { addError } = useNotificationError();
  const dispatch = useDispatch();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  return useMutation({
    mutationKey: [QueryKeys.REQUEST_ID_CARD],
    mutationFn: (userInfo: IUserInfo) =>
      requestIdService(userInfo, activeCoverage),
    onSuccess: (data: IRequestIdResponse) => {
      dispatch(
        updateRequestedAt({
          requestedAt: data?.data?.idCardRequestedAt,
          isLoaded: true,
        }),
      );
      if (
        lowerCaseComparison(
          data?.summary?.status_message,
          API_STATUS_MESSAGE.SUCCESS,
        )
      ) {
        addError({
          message: data?.data?.message,
          type: ErrorNotificationType.INFO,
        });
      } else if (
        lowerCaseComparison(
          data?.summary?.status_message,
          API_STATUS_MESSAGE.BLOCKED,
        )
      ) {
        addError({
          message:
            data?.data?.message ||
            "We couldn't process your request right now. Please try again.",
          type: ErrorNotificationType.ERROR,
        });
      } else {
        addError({
          message:
            "We couldn't process your request right now. Please try again.",
          type: ErrorNotificationType.ERROR,
        });
      }
    },
    onError(error: unknown) {
      const errorMessage = getErrorMessage(error);
      addError({
        message: errorMessage,
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};
